import { confirmProblemReportAttachment, reserveProblemReportAttachment } from '../../api/generated/problem-reports/problem-reports'
import type { ProblemReportAttachmentRead } from '../../api/generated/model/problemReportAttachmentRead'
import type { ProblemReportUploadGrant } from '../../api/generated/model/problemReportUploadGrant'
import type { ProblemReportUploadRequest } from '../../api/generated/model/problemReportUploadRequest'
import { reportHostState, settleReportRequest } from '../../lib/problem-reporting/context'
import type { ReportRequestContext } from '../../lib/problem-reporting/context'
import type { SubmissionError } from './submission'

export interface SubmissionImage {
  kind: 'image' | 'video'
  key: string
  previewUrl: string | null
  status: 'staged' | 'reserving' | 'uploading' | 'confirming' | 'attached' | 'failed'
  confirmation: 'not_attempted' | 'unresolved' | 'rejected' | 'confirmed'
  error: Readonly<SubmissionError> | null
  attachment: Readonly<ProblemReportAttachmentRead> | null
}
export interface ImageWork {
  view: SubmissionImage
  blob: Blob | null
  metadata: ProblemReportUploadRequest
  grant: ProblemReportUploadGrant | null
  running: boolean
}
export function releaseImage(image: ImageWork) {
  if (image.view.previewUrl) URL.revokeObjectURL(image.view.previewUrl)
  image.view = { ...image.view, previewUrl: null }
  image.blob = null
}
export async function uploadReportImage(
  image: ImageWork, reportId: string, context: ReportRequestContext, signal: AbortSignal,
  changed: () => void,
) {
  const options = { signal, headers: { 'X-Problem-Report-Context': context.identity.membershipId } }
  const stage = (status: SubmissionImage['status']) => { image.view = { ...image.view, status, error: null }; changed() }
  await settleReportRequest(context, signal)
  if (image.view.confirmation === 'not_attempted') {
    const host = reportHostState()
    if (host.phase !== 'ready' || !host.policy?.enabled || !host.policy.can_self) {
      throw { problem: { code: 'reporting_unavailable' } }
    }
    if (!image.grant || Date.parse(image.grant.expires_at) <= Date.now()) {
      stage('reserving')
      const response = await reserveProblemReportAttachment(reportId, image.metadata, options)
      await settleReportRequest(context, signal)
      if (response.status !== 200 && response.status !== 201) throw { status: response.status, problem: response.data }
      image.grant = response.data
    }
    stage('uploading')
    const response = await fetch(image.grant.upload_url, {
      method: 'PUT', headers: image.grant.required_headers, body: image.blob, signal,
      credentials: 'omit', redirect: 'error', referrerPolicy: 'no-referrer',
    })
    await settleReportRequest(context, signal)
    if (!response.ok) throw { status: response.status, problem: { code: 'image_transfer_failed' } }
    image.view = { ...image.view, confirmation: 'unresolved' }
  }
  const grant = image.grant!
  stage('confirming')
  try {
    const response = await confirmProblemReportAttachment(reportId, {
      attachment_key: image.metadata.attachment_key, session_id: grant.session_id,
      original_sha256: image.metadata.original_sha256,
    }, options)
    await settleReportRequest(context, signal)
    if (response.status !== 200 && response.status !== 201) throw { status: response.status, problem: response.data }
    image.view = { ...image.view, status: 'attached', confirmation: 'confirmed', attachment: Object.freeze({ ...response.data }), error: null }
    releaseImage(image); image.grant = null; changed()
  } catch (error) {
    const failure = error as { status?: number; problem?: { code?: string } }
    const code = failure.problem?.code
    if (failure.status === 409 && code === 'report_attachment_expired') {
      image.view = { ...image.view, confirmation: 'not_attempted' }; image.grant = null
    } else if ((failure.status === 409 && code === 'report_attachment_closed') || (failure.status === 422 && code === 'report_image_invalid')) {
      // These checks follow the durable receipt lookup; auth/policy denials do not.
      image.view = { ...image.view, confirmation: 'rejected' }
    }
    throw error
  }
}
